import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { toastMessage } from '../../i18n/toastMessages';
import {
  changePassword,
  getProfile,
  updateProfile,
  UserProfile,
} from '../../services/profileService';
import '../../pages/billing/billing.css';

interface ProfileSectionProps {
  /** Bumped by the parent so the sidebar greeting re-reads the saved name. */
  onProfileSaved?: () => void;
}

const MIN_PASSWORD_LENGTH = 8;

const readStoredUser = (): Record<string, unknown> => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    return stored && typeof stored === 'object' ? stored : {};
  } catch {
    return {};
  }
};

const toInputValue = (value: string | null): string => value ?? '';

/**
 * Profile settings section: edit first/last/studio name and reset the
 * password. Rendered inside the dashboard (not a route) — checkout-style
 * actions don't apply here, so both cards save in place with toasts.
 */
const ProfileSection: React.FC<ProfileSectionProps> = ({ onProfileSaved }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [studioName, setStudioName] = useState('');
  const [email, setEmail] = useState('');
  // Last server-known values, for dirty-checking the save payload.
  const [savedProfile, setSavedProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  // Password form stays collapsed behind a CTA until needed.
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Prefill instantly from the cached user so the form is never blank,
  // then overwrite with the server profile when it arrives.
  useEffect(() => {
    const stored = readStoredUser();
    setFirstName(
      toInputValue(
        typeof stored.firstName === 'string' ? stored.firstName : null
      )
    );
    setLastName(
      toInputValue(typeof stored.lastName === 'string' ? stored.lastName : null)
    );
    setStudioName(
      toInputValue(
        typeof stored.studioName === 'string' ? stored.studioName : null
      )
    );
    setEmail(
      toInputValue(typeof stored.email === 'string' ? stored.email : null)
    );

    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      setLoadError('Please log in to manage your profile.');
      return;
    }
    let cancelled = false;
    void getProfile(token)
      .then((profile) => {
        if (cancelled) return;
        setFirstName(toInputValue(profile.firstName));
        setLastName(toInputValue(profile.lastName));
        setStudioName(toInputValue(profile.studioName));
        setEmail(toInputValue(profile.email));
        setSavedProfile(profile);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError('Could not load your profile. Showing saved details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSaveProfile = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error(toastMessage('common.loginRequired'));
      return;
    }
    const trimmed = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      studioName: studioName.trim(),
    };
    if (!trimmed.firstName) {
      toast.error(toastMessage('profile.firstNameRequired'));
      return;
    }
    // Send only edited fields (partial update).
    const payload: Record<string, string> = {};
    if (
      !savedProfile ||
      trimmed.firstName !== toInputValue(savedProfile.firstName)
    )
      payload.firstName = trimmed.firstName;
    if (
      !savedProfile ||
      trimmed.lastName !== toInputValue(savedProfile.lastName)
    )
      payload.lastName = trimmed.lastName;
    if (
      !savedProfile ||
      trimmed.studioName !== toInputValue(savedProfile.studioName)
    )
      payload.studioName = trimmed.studioName;
    if (Object.keys(payload).length === 0) {
      toast.info(toastMessage('profile.noChanges'));
      return;
    }

    setSavingProfile(true);
    try {
      const updated = await updateProfile(token, payload);
      setSavedProfile(updated);
      // Merge into the cached user so the sidebar greeting updates instantly.
      const stored = readStoredUser();
      localStorage.setItem(
        'user',
        JSON.stringify({
          ...stored,
          ...(updated.firstName !== null
            ? { firstName: updated.firstName }
            : {}),
          ...(updated.lastName !== null ? { lastName: updated.lastName } : {}),
          ...(updated.studioName !== null
            ? { studioName: updated.studioName }
            : {}),
          ...(updated.email !== null ? { email: updated.email } : {}),
        })
      );
      onProfileSaved?.();
      toast.success(toastMessage('profile.updated'));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : toastMessage('profile.updateFailed')
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Fill in all three password fields.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(
        `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from the current one.');
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error(toastMessage('common.loginRequired'));
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(token, { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError(null);
      setShowPasswordForm(false);
      toast.success(toastMessage('profile.passwordChanged'));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : toastMessage('profile.passwordChangeFailed');
      setPasswordError(message);
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <section className="dashboard-section profile-section">
      <div className="section-header">
        <div>
          <h2>Profile</h2>
          <p className="section-subtitle">Manage your account details</p>
        </div>
      </div>

      {loading ? (
        <div
          className="billing-card"
          aria-busy="true"
          aria-label="Loading profile"
        >
          <div className="billing-skeleton-line billing-skeleton-line-title" />
          <div className="billing-skeleton-line" />
          <div className="billing-skeleton-line" />
          <div className="billing-skeleton-line billing-skeleton-line-short" />
        </div>
      ) : (
        <>
          {loadError && (
            <p className="billing-error" role="alert">
              {loadError}
            </p>
          )}
          <div className="billing-card" aria-label="Profile details">
            <h3>Details</h3>
            <div className="profile-field">
              <label htmlFor="profile-first-name">First name</label>
              <input
                id="profile-first-name"
                type="text"
                className="profile-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </div>
            <div className="profile-field">
              <label htmlFor="profile-last-name">Last name</label>
              <input
                id="profile-last-name"
                type="text"
                className="profile-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
            <div className="profile-field">
              <label htmlFor="profile-studio-name">Studio name</label>
              <input
                id="profile-studio-name"
                type="text"
                className="profile-input"
                value={studioName}
                onChange={(e) => setStudioName(e.target.value)}
                autoComplete="organization"
              />
            </div>
            <div className="profile-field">
              <label htmlFor="profile-email">Email</label>
              <input
                id="profile-email"
                type="email"
                className="profile-input"
                value={email}
                disabled
                title="Email cannot be changed"
              />
            </div>
            <div className="profile-actions">
              <button
                type="button"
                className="billing-primary"
                onClick={handleSaveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? 'Saving…' : 'Save changes'}
              </button>
              {!showPasswordForm && (
                <button
                  type="button"
                  className="billing-secondary"
                  onClick={() => {
                    setPasswordError(null);
                    setShowPasswordForm(true);
                  }}
                  aria-expanded={showPasswordForm}
                >
                  Change password
                </button>
              )}
            </div>
          </div>

          {showPasswordForm && (
            <div className="billing-card" aria-label="Reset password">
              <h3>Reset password</h3>
              <div className="profile-field">
                <label htmlFor="profile-current-password">
                  Current password
                </label>
                <input
                  id="profile-current-password"
                  type="password"
                  className="profile-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <div className="profile-field">
                <label htmlFor="profile-new-password">New password</label>
                <input
                  id="profile-new-password"
                  type="password"
                  className="profile-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="profile-field">
                <label htmlFor="profile-confirm-password">
                  Confirm new password
                </label>
                <input
                  id="profile-confirm-password"
                  type="password"
                  className="profile-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              {passwordError && (
                <p className="profile-error" role="alert">
                  {passwordError}
                </p>
              )}
              <button
                type="button"
                className="billing-primary"
                onClick={handleChangePassword}
                disabled={changingPassword}
              >
                {changingPassword ? 'Changing…' : 'Change password'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default ProfileSection;
